import React, { useMemo, useRef, useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { colors3 } from "../../colors/color";
import CustomizeLegend from "../../components/CustomizeLegend";
import Printer2 from "../../components/Printer2";
import GraphLoader from "../../components/GraphLoader";
import { Button, ButtonGroup, Chip } from "@mui/material";

const ForecastPlantAvailability2 = ({ 
  forecastedPlantAvailability, 
  loading,
  selectedPeriod: propSelectedPeriod,
  onPeriodChange,
  selectedLebel // Add this prop to get dateRangeType
}) => {
  const graphRef = useRef(null);
  const [showBarData, setShowBarData] = useState({
    plantAvailability: true,
    forecastedPlantAvailability: true,
    all: true,
  });

  // Use prop for selected period from parent
  const [filterBelow100, setFilterBelow100] = useState(false);

  // Get the current date range type from selectedLebel
  const currentDateRangeType = selectedLebel?.dateRangeType || "month";

  // Dynamic time period options based on dateRangeType
  const getTimePeriods = () => {
    switch (currentDateRangeType) {
      case "day":
        return [
          { label: "Historical", value: "historical", description: "View past data (daily)" },
          { label: "Next 7 Days", value: "7days", description: "Forecast next 7 days" },
          { label: "Next 30 Days", value: "30days", description: "Forecast next 30 days" },
        ];
      case "month":
        return [
          { label: "Historical", value: "historical", description: "View past data (monthly)" },
          { label: "Next 6 Months", value: "6months", description: "Forecast next 6 months" },
          { label: "Next Year", value: "year", description: "Forecast next year" },
        ];
      case "year":
        return [
          { label: "Historical", value: "historical", description: "View past data (yearly)" },
          { label: "Next 6 Years", value: "6years", description: "Forecast next 6 years" },
          { label: "Next 10 Years", value: "10years", description: "Forecast next 10 years" },
        ];
      default:
        return [
          { label: "Historical", value: "historical", description: "View past data" },
          { label: "Next 6 Months", value: "6months", description: "Forecast next 6 months" },
          { label: "Next Year", value: "year", description: "Forecast next year" },
        ];
    }
  };

  const timePeriods = getTimePeriods();

  // Helper function to get slice limit based on period and date range type
  const getSliceLimit = (period) => {
    switch (period) {
      case "7days":
        return 7;
      case "30days":
        return 30;
      case "6months":
        return 6;
      case "year":
        return 12;
      case "6years":
        return 6;
      case "10years":
        return 10;
      default:
        return null;
    }
  };

  // Helper function to safely format numbers
  const formatNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return "N/A";
    }
    
    // Convert to number if it's a string
    const num = typeof value === 'string' ? parseFloat(value) : value;
    
    // Check if it's a valid number
    if (typeof num !== 'number' || isNaN(num)) {
      return "N/A";
    }
    
    return num.toFixed(2);
  };

  // Custom tooltip component to show difference with colors
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const periodOverPeriodChange = data["Period Over Period Change"];
      const periodOverPeriodPercentage = data["Period Over Period Percentage"];

      // Get the period label based on date range type
      const periodLabel = currentDateRangeType === "day" ? "Day" : 
                          currentDateRangeType === "year" ? "Year" : "Month";

      return (
        <div
          style={{
            backgroundColor: "white",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "5px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          }}
        >
          <p style={{ margin: "0 0 5px 0", fontWeight: "bold" }}>
            {label}
          </p>
          {payload.map((entry, index) => (
            <p
              key={index}
              style={{
                margin: "2px 0",
                color: entry.color,
                fontSize: "0.8rem",
              }}
            >
              {entry.name}: <strong>{formatNumber(entry.value)}%</strong>
            </p>
          ))}
          {periodOverPeriodChange !== null && periodOverPeriodPercentage !== null && (
            <p
              style={{
                margin: "5px 0 0 0",
                padding: "3px 6px",
                borderRadius: "3px",
                backgroundColor: periodOverPeriodChange >= 0 ? "#f6ffed" : "#fff2f0",
                border: `1px solid ${periodOverPeriodChange >= 0 ? "#b7eb8f" : "#ffccc7"}`,
                fontSize: "0.75rem",
                fontWeight: "bold",
                color: periodOverPeriodChange >= 0 ? "#389e0d" : "#a8071a",
              }}
            >
              vs Previous {periodLabel}: 
              <span style={{ marginLeft: "5px" }}>
                {periodOverPeriodChange >= 0 ? "+" : ""}{formatNumber(periodOverPeriodChange)}% 
                ({periodOverPeriodChange >= 0 ? "+" : ""}{formatNumber(periodOverPeriodPercentage)}%)
              </span>
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Handle period change - notify parent
  const handlePeriodChange = (period) => {
    if (onPeriodChange) {
      onPeriodChange(period);
    }
  };

  // Handle filter toggle
  const handleFilterToggle = () => {
    setFilterBelow100(!filterBelow100);
  };

  const minMaxRangeAndGraphData = useMemo(() => {
    if (forecastedPlantAvailability && forecastedPlantAvailability?.length > 0) {
      // Filter data based on selected time period
      let filteredData = [...forecastedPlantAvailability];
      
      if (propSelectedPeriod === "historical") {
        // Show historical data (past actual values)
        filteredData = filteredData.filter(item => 
          item["Plant Availability"] != null
        );
      } else {
        // Show forecast data
        const sliceLimit = getSliceLimit(propSelectedPeriod);
        filteredData = filteredData.filter(item => 
          item["Forecasted Plant Availability"] != null
        );
        if (sliceLimit) {
          filteredData = filteredData.slice(0, sliceLimit);
        }
      }

      // Create the base graph data with difference calculation
      const baseGraphData = filteredData?.reduce((acc, curr, index, array) => {
        if (curr["Plant Availability"] || curr["Forecasted Plant Availability"]) {
          let periodOverPeriodChange = null;
          let periodOverPeriodPercentage = null;
          
          // Calculate difference from previous period
          if (index > 0) {
            const currentAvailability = curr["Plant Availability"] || curr["Forecasted Plant Availability"];
            const prevAvailability = array[index - 1]["Plant Availability"] || array[index - 1]["Forecasted Plant Availability"];
            
            if (currentAvailability != null && prevAvailability != null) {
              // Convert to numbers if needed
              const currentValue = typeof currentAvailability === 'string' 
                ? parseFloat(currentAvailability) 
                : currentAvailability;
              const prevValue = typeof prevAvailability === 'string' 
                ? parseFloat(prevAvailability) 
                : prevAvailability;
              
              periodOverPeriodChange = currentValue - prevValue;
              periodOverPeriodPercentage = prevValue !== 0 
                ? (periodOverPeriodChange / prevValue) * 100
                : 0;
            }
          }

          // Convert values to numbers
          const plantAvailability = curr["Plant Availability"] != null 
            ? (typeof curr["Plant Availability"] === 'string' 
                ? parseFloat(curr["Plant Availability"]) 
                : curr["Plant Availability"])
            : null;
          
          const forecastedAvailability = curr["Forecasted Plant Availability"] != null 
            ? (typeof curr["Forecasted Plant Availability"] === 'string' 
                ? parseFloat(curr["Forecasted Plant Availability"]) 
                : curr["Forecasted Plant Availability"])
            : null;

          acc.push({
            Date: curr["Date"],
            "Plant Availability": plantAvailability,
            "Forecasted Plant Availability": forecastedAvailability,
            // Add difference data for tooltip (ensure numbers)
            "Period Over Period Change": periodOverPeriodChange !== null ? Number(periodOverPeriodChange.toFixed(2)) : null,
            "Period Over Period Percentage": periodOverPeriodPercentage !== null ? Number(periodOverPeriodPercentage.toFixed(2)) : null,
          });
        }
        return acc;
      }, []) || [];

      // Apply filter if enabled
      const filteredGraphData = filterBelow100 
        ? baseGraphData.filter(item => {
            const plantAvailability = item["Plant Availability"];
            const forecastedAvailability = item["Forecasted Plant Availability"];
            
            // Check if either Plant Availability or Forecasted Plant Availability is less than 100
            const plantBelow100 = plantAvailability !== null && plantAvailability < 100;
            const forecastedBelow100 = forecastedAvailability !== null && forecastedAvailability < 100;
            
            return plantBelow100 || forecastedBelow100;
          })
        : baseGraphData;

      // Calculate min/max based on filtered data
      const allValues = [];
      filteredGraphData.forEach(item => {
        if (item["Plant Availability"] != null) allValues.push(item["Plant Availability"]);
        if (item["Forecasted Plant Availability"] != null) allValues.push(item["Forecasted Plant Availability"]);
      });

      // Get the unit label based on date range type
      const unitLabel = currentDateRangeType === "day" ? "days" : 
                        currentDateRangeType === "year" ? "years" : "months";

      return {
        graphData: filteredGraphData,
        min: allValues.length > 0 ? Math.floor(Math.min(...allValues)) : 0,
        max: allValues.length > 0 ? Math.ceil(Math.max(...allValues)) : 100,
        totalCount: baseGraphData.length,
        filteredCount: filteredGraphData.length,
        unitLabel: unitLabel,
      };
    }
    return { graphData: [], min: 0, max: 100, totalCount: 0, filteredCount: 0, unitLabel: "months" };
  }, [forecastedPlantAvailability, propSelectedPeriod, filterBelow100, currentDateRangeType]);

  // Custom label formatter for bar labels
  const renderBarLabel = (value) => {
    const formatted = formatNumber(value);
    return formatted === "N/A" ? "" : formatted;
  };

  // Get display unit for the title
  const getDisplayUnit = () => {
    switch (currentDateRangeType) {
      case "day":
        return "Daily";
      case "month":
        return "Monthly";
      case "year":
        return "Yearly";
      default:
        return "Monthly";
    }
  };

  return (
    <>
      {/* Time Period Selection */}
      <div
        style={{
          padding: "1rem 1.5rem",
          position: "sticky",
          left: "0",
          right: "0",
          top: "0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          textAlign: "center",
          zIndex: "2",
          backgroundColor: "white",
          borderBottom: "1px solid #f0f0f0",
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        {/* Left side - Time Period Selection */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "0.5rem" }}>
          <label
            style={{
              fontSize: "0.9rem",
              fontWeight: 500,
              color: "#333",
            }}
          >
            Time Period ({getDisplayUnit()}):
          </label>
          <ButtonGroup
            variant="outlined"
            size="small"
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
          >
            {timePeriods.map((period) => (
              <Button
                key={period.value}
                onClick={() => handlePeriodChange(period.value)}
                variant={propSelectedPeriod === period.value ? "contained" : "outlined"}
                style={{
                  backgroundColor: propSelectedPeriod === period.value ? "#1890ff" : "transparent",
                  color: propSelectedPeriod === period.value ? "white" : "#666",
                  borderColor: "#d0d0d0",
                  fontSize: "0.8rem",
                  padding: "4px 12px",
                  minWidth: "90px",
                }}
                title={period.description}
              >
                {period.label}
              </Button>
            ))}
          </ButtonGroup>
        </div>

        {/* Center - Title */}
        <div style={{ flex: 1, textAlign: "center" }}>
          <h5 style={{ margin: "0 0 5px 0" }}>
            Forecasted Plant Availability (%) - {getDisplayUnit()} Data
            {filterBelow100 && (
              <Chip
                label="Filtered"
                size="small"
                style={{
                  marginLeft: "8px",
                  backgroundColor: "#fff2f0",
                  color: "#a8071a",
                  borderColor: "#ffccc7",
                  fontSize: "0.7rem",
                  height: "20px",
                }}
                variant="outlined"
              />
            )}
          </h5>
          <p style={{ margin: 0, fontSize: "0.8rem", color: "#666" }}>
            {timePeriods.find(p => p.value === propSelectedPeriod)?.description}
          </p>
        </div>

        {/* Right side - Filter and Printer */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.5rem" }}>
          {/* Filter Toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <label
              htmlFor="availability-filter"
              style={{
                fontSize: "0.85rem",
                fontWeight: 500,
                color: "#333",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <input
                id="availability-filter"
                type="checkbox"
                checked={filterBelow100}
                onChange={handleFilterToggle}
                style={{
                  width: "16px",
                  height: "16px",
                  cursor: "pointer",
                  accentColor: "#1890ff",
                }}
              />
              Below 100% Only
            </label>

            {/* Filter badge showing counts */}
            {filterBelow100 && minMaxRangeAndGraphData.totalCount > 0 && (
              <Chip
                label={`${minMaxRangeAndGraphData.filteredCount}/${minMaxRangeAndGraphData.totalCount} ${minMaxRangeAndGraphData.unitLabel}`}
                size="small"
                style={{
                  fontSize: "0.7rem",
                  backgroundColor: "#e6f7ff",
                  color: "#1890ff",
                  borderColor: "#91d5ff",
                  height: "22px",
                }}
                variant="outlined"
              />
            )}
          </div>

          {/* Printer */}
          <div style={{ display: "flex", alignItems: "center" }}>
            <Printer2
              dataSetDetail={{
                data: minMaxRangeAndGraphData.graphData,
                name: "Plant Availability",
                ref: graphRef,
                refCurrent: graphRef.current?.container,
              }}
            />
          </div>
        </div>
      </div>

      {/* Custom Legend Container - Fixed Position */}
      <div
        style={{
          position: "sticky",
          left: "0",
          top: "80px",
          zIndex: "10",
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          padding: "10px 16px",
          borderBottom: "1px solid #f0f0f0",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginBottom: "10px",
        }}
      >
        <CustomizeLegend
          active={false}
          payload={[]}
          label={""}
          LegendValues={["Plant Availability", "Forecasted Plant Availability"]}
          data={showBarData}
          setData={setShowBarData}
          styles={{ 
            fontSize: "0.85rem", 
            display: "flex", 
            justifyContent: "center", 
            alignItems: "center",
            gap: "30px",
            flexWrap: "wrap"
          }}
        />
      </div>

      {/* Graph Container */}
      <div
        style={{
          minWidth: "100%",
          width:
            forecastedPlantAvailability?.length > 0 ? 2*forecastedPlantAvailability.length * 15 + "px" : "100%",
          paddingTop: "10px",
        }}
        className="inverter-efficiency-responsive-container"
      >
        <ResponsiveContainer width="100%" height={400}>
          {loading ? (
            <div
              style={{
                position: "absolute",
                top: "0",
                bottom: "0",
                right: "0",
                left: "0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <GraphLoader />
            </div>
          ) : minMaxRangeAndGraphData.graphData?.length > 0 ? (
            <ComposedChart
              barCategoryGap={"20%"}
              ref={graphRef}
              margin={{
                top: 15,
                bottom: 5,
                left: 5,
                right: 5,
              }}
              data={minMaxRangeAndGraphData.graphData || []}
            >
              <XAxis
                fontSize={"0.8rem"}
                fontWeight={600}
                dataKey="Date"
                tickLine={false}
                axisLine={false}
                tickMargin={5}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <CartesianGrid stroke="grey" strokeWidth={0.3} />

              <YAxis
                type="number"
                fontSize={"0.8rem"}
                fontWeight={600}
                tickLine={false}
                tickMargin={5}
                tickCount={6}
                domain={[0, 100]} // Plant availability is typically between 0-100%
                label={{
                  value: "Availability (%)",
                  angle: -90,
                  position: "insideLeft",
                  offset: -10,
                  style: { fontSize: "0.8rem", fontWeight: "bold" },
                }}
              />

              {/* Use custom tooltip */}
              <Tooltip
                cursor={{ fill: "none" }}
                content={<CustomTooltip />}
                contentStyle={{ fontSize: "0.7rem" }}
              />

              <Bar
                hide={!showBarData.plantAvailability}
                dataKey="Plant Availability"
                fill={colors3[0]}
                onTransitionEnd={"1s all"}
                maxBarSize={30}
              >
                <LabelList
                  style={{ zIndex: 6 }}
                  dataKey="Plant Availability"
                  position="top"
                  fontSize={"0.8rem"}
                  fontWeight={600}
                  fontFamily="Cambria, Cochin, Georgia, Times, 'Times New Roman', serif;"
                  formatter={renderBarLabel}
                />
              </Bar>

              <Bar
                hide={!showBarData.forecastedPlantAvailability}
                dataKey="Forecasted Plant Availability"
                fill="red"
                onTransitionEnd={"1s all"}
                maxBarSize={30}
              >
                <LabelList
                  style={{ zIndex: 6 }}
                  dataKey="Forecasted Plant Availability"
                  position="top"
                  fontSize={"0.8rem"}
                  fontWeight={600}
                  fontFamily="Cambria, Cochin, Georgia, Times, 'Times New Roman', serif;"
                  formatter={renderBarLabel}
                />
              </Bar>
            </ComposedChart>
          ) : (
            <div
              style={{
                height: "100%",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: "1rem",
              }}
            >
              <h3>
                {filterBelow100 
                  ? "No data matches the filter criteria" 
                  : "No data available for the selected period"
                }
              </h3>
              {filterBelow100 && (
                <button
                  onClick={() => setFilterBelow100(false)}
                  style={{
                    padding: "0.5rem 1rem",
                    backgroundColor: "#1890ff",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                  }}
                >
                  Show All Data
                </button>
              )}
            </div>
          )}
        </ResponsiveContainer>
      </div>

      {/* Summary Section */}
      {minMaxRangeAndGraphData.graphData?.length > 0 && (
        <div
          style={{
            padding: "1rem",
            borderTop: "1px solid #f0f0f0",
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "1rem",
            marginTop: "1rem",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "0.8rem", color: "#666", marginBottom: "4px" }}>
              Total {minMaxRangeAndGraphData.unitLabel === "days" ? "Days" : 
                       minMaxRangeAndGraphData.unitLabel === "years" ? "Years" : "Months"}
            </div>
            <div style={{ fontSize: "1.2rem", fontWeight: "bold", color: "#1890ff" }}>
              {minMaxRangeAndGraphData.totalCount}
            </div>
          </div>
          
          {filterBelow100 && (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "0.8rem", color: "#666", marginBottom: "4px" }}>
                {minMaxRangeAndGraphData.unitLabel === "days" ? "Days" : 
                 minMaxRangeAndGraphData.unitLabel === "years" ? "Years" : "Months"} Below 100%
              </div>
              <div style={{ fontSize: "1.2rem", fontWeight: "bold", color: "#ff4d4f" }}>
                {minMaxRangeAndGraphData.filteredCount}
              </div>
            </div>
          )}
          
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "0.8rem", color: "#666", marginBottom: "4px" }}>
              Selected Period
            </div>
            <div style={{ fontSize: "1rem", fontWeight: "500", color: "#333" }}>
              {timePeriods.find(p => p.value === propSelectedPeriod)?.label}
            </div>
          </div>

          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "0.8rem", color: "#666", marginBottom: "4px" }}>
              Data Type
            </div>
            <div style={{ fontSize: "1rem", fontWeight: "500", color: "#333" }}>
              {getDisplayUnit()}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ForecastPlantAvailability2;